import type { ClaimFilters } from '../types/claims.types';
import type { TextFilterKey } from '../utils/claim-filters';
import { FilterCategory, FilterDateRange, FilterText } from './FilterControls';

interface FilterFieldsProps {
  readonly draft: ClaimFilters;
  readonly statuses: readonly string[];
  readonly dateErrors: { readonly service: string | null; readonly payment: string | null };
  readonly amountErrors: {
    readonly billedAmount: string | null;
    readonly medicareAllowedAmount: string | null;
    readonly paidAmount: string | null;
  };
  readonly textErrors: Record<TextFilterKey, string | null>;
  set: <K extends keyof ClaimFilters>(key: K, value: ClaimFilters[K]) => void;
  toggleStatus: (status: string) => void;
}

/** Every filter category of the panel (US-014 AC-002), each independently expandable. */
export function FilterFields({
  draft,
  statuses,
  dateErrors,
  amountErrors,
  textErrors,
  set,
  toggleStatus,
}: FilterFieldsProps): React.JSX.Element {
  return (
    <div className="divide-y divide-line">
        <FilterText label="Claim Number" value={draft.claimNumber} error={textErrors.claimNumber} onChange={(v): void => { set('claimNumber', v); }} />
        <FilterText label="NPI" value={draft.npi} error={textErrors.npi} onChange={(v): void => { set('npi', v); }} />
        {/* TODO: ICN, Location and Svc Code are mock-only fields (US-014 AC-002). */}
        <FilterText label="ICN" value={draft.icn} error={textErrors.icn} onChange={(v): void => { set('icn', v); }} />
        <FilterText label="Provider Name" value={draft.location} onChange={(v): void => { set('location', v); }} />
        <FilterText label="Proc Code" value={draft.procCode} error={textErrors.procCode} onChange={(v): void => { set('procCode', v); }} />
        <FilterText label="Svc Code" value={draft.svcCode} error={textErrors.svcCode} onChange={(v): void => { set('svcCode', v); }} />

        <FilterDateRange
          label="Date of Service"
          from={draft.serviceDateFrom}
          to={draft.serviceDateTo}
          error={dateErrors.service}
          onChangeFrom={(v): void => { set('serviceDateFrom', v); }}
          onChangeTo={(v): void => { set('serviceDateTo', v); }}
        />

        <FilterCategory label="Status" defaultOpen>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="sr-only">Claim status</legend>
            {statuses.map((status) => (
              <label key={status} className="flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={draft.statuses.includes(status)}
                  onChange={(): void => { toggleStatus(status); }}
                  className="accent-brand-600 h-3.5 w-3.5"
                />
                {status}
              </label>
            ))}
          </fieldset>
        </FilterCategory>

        <FilterText label="Reason" value={draft.reason} onChange={(v): void => { set('reason', v); }} />
        <FilterText label="Billed Amt (minimum)" value={draft.billedAmount} inputMode="decimal" error={amountErrors.billedAmount} onChange={(v): void => { set('billedAmount', v); }} />
        <FilterText label="Medicare Allowed Amt (minimum)" value={draft.medicareAllowedAmount} inputMode="decimal" error={amountErrors.medicareAllowedAmount} onChange={(v): void => { set('medicareAllowedAmount', v); }} />
        <FilterText label="Medicare Paid Amt (minimum)" value={draft.paidAmount} inputMode="decimal" error={amountErrors.paidAmount} onChange={(v): void => { set('paidAmount', v); }} />

        <FilterDateRange
          label="Payment Date"
          from={draft.paymentDateFrom}
          to={draft.paymentDateTo}
          error={dateErrors.payment}
          onChangeFrom={(v): void => { set('paymentDateFrom', v); }}
          onChangeTo={(v): void => { set('paymentDateTo', v); }}
        />

        <FilterText label="Check #" value={draft.checkNumber} error={textErrors.checkNumber} onChange={(v): void => { set('checkNumber', v); }} />
      </div>
  );
}
